import { DEFAULT_LOCALE } from '../config';

export const getTodayStart = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const getTodayEnd = (): Date => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today;
};

/**
 * Format date to ISO date string (YYYY-MM-DD) using LOCAL timezone.
 * Important: Uses local date components, not UTC, to avoid date shifts around midnight.
 */
export const formatDateISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateDisplay = (date: Date): string => {
  return date.toLocaleDateString(DEFAULT_LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Check if dateString is today in LOCAL timezone.
 * Converts ISO timestamp to local Date and compares with today.
 */
export const isToday = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  return isSameDay(date, today);
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Check if ISO dateString falls on the same day as the given date in LOCAL timezone.
 */
export const isDateStringOnDay = (dateString: string, targetDate: Date): boolean => {
  const date = new Date(dateString);
  return isSameDay(date, targetDate);
};

/**
 * Check if ISO dateString falls in the same month as the given date in LOCAL timezone.
 */
export const isDateStringInMonth = (dateString: string, targetDate: Date): boolean => {
  const date = new Date(dateString);
  return isSameMonth(date, targetDate);
};

export const isSameMonth = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth()
  );
};

/**
 * Get the number of working days (Monday-Friday) in a given month.
 */
export const getWorkingDaysInMonth = (date: Date): number => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  let workingDays = 0;
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
    const dayOfWeek = d.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays++;
    }
  }
  return workingDays;
};

/**
 * Get localized month name for a given date.
 */
export const getMonthName = (date: Date): string => {
  return date.toLocaleDateString(DEFAULT_LOCALE, { month: 'long', year: 'numeric' });
};
